import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { formatDate, humanize } from "../../../utils/format.ts";
import type { DateBasis, EmploymentStint, TitlePeriod } from "../../../api/client.ts";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import EmptyState from "../../../components/EmptyState.tsx";
import Panel from "../../../components/Panel.tsx";
import { profileSourceLabel } from "./profileFormat.ts";

interface ContactTimelineCardProps {
  employment: EmploymentStint[];
}

/**
 * Renders a date according to how we came by it.
 *
 * This is the whole point of carrying a basis. Only "declared" is a date a
 * source actually stated; "observed" just means when we first saw the fact, and
 * showing that as "Since Jan 2024" would assert a start date nobody claimed —
 * which is exactly the bug this replaces.
 */
function formatBoundary(date: string | null, basis: DateBasis | null): string {
  if (!date) return "date unknown";
  const month = formatDate(date, "month", date);
  switch (basis) {
    case "declared":
      return month;
    case "observed":
      return `known since ${month}`;
    case "source_changed_at":
      return `as of ${month}`;
    case "inferred":
      return `by ${month}`;
    default:
      return `known since ${month}`;
  }
}

function formatRange(stint: EmploymentStint): string {
  const start = formatBoundary(stint.started_on, stint.started_basis);
  if (stint.is_current) return `${start} – present`;
  if (!stint.ended_on) return start;
  return `${start} – ${formatBoundary(stint.ended_on, stint.ended_basis)}`;
}

function titleLabel(period: TitlePeriod, stint: EmploymentStint): string {
  // Employment carries a free-text title; statutory roles use the canonical
  // role label instead, since they have no title of their own.
  return period.title ?? stint.role_title_en ?? stint.relation_type;
}

function CompanyName({ stint }: { stint: EmploymentStint }) {
  const { tenantPath } = useTenant();
  if (stint.company_id) {
    return (
      <Link component={RouterLink} to={tenantPath(`/companies/${stint.company_id}`)}>
        {stint.company_name ?? stint.company_id}
      </Link>
    );
  }
  return (
    <Tooltip title="This employer could not be matched to a company in the database yet">
      <Typography component="span" sx={{ fontStyle: "italic" }}>
        {stint.company_label ?? "Unknown company"}
      </Typography>
    </Tooltip>
  );
}

export default function ContactTimelineCard({ employment }: ContactTimelineCardProps) {
  return (
    <Panel title="Work history" count={employment.length} sx={{ mb: 3 }}>
      {employment.length === 0 ? (
        <EmptyState title="No roles or employment on record" />
      ) : (
        <Stack spacing={2.5}>
          {employment.map((stint) => (
            <Box
              key={stint.stint_id}
              sx={{
                borderLeft: 2,
                borderColor: stint.is_current ? "success.main" : "divider",
                pl: 2,
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <CompanyName stint={stint} />
                </Typography>
                {stint.is_current && <Chip label="Current" color="success" size="small" />}
                {stint.sources.map((source) => (
                  <Chip
                    key={source}
                    // Bought data names its provider ("GetLeads"); other sources as stored.
                    label={source.startsWith("enrichment_") ? profileSourceLabel(source) : source}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>

              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {formatRange(stint)}
              </Typography>

              {/* Title periods sit inside the engagement: a promotion is not
                  a new job, and rendering it as one is what this fixes. */}
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {stint.titles.map((period) => (
                  <Stack
                    key={period.edge_id}
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "baseline", flexWrap: "wrap" }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 200 }}>
                      {titleLabel(period, stint)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {period.is_current
                        ? `${formatBoundary(period.from_date, period.from_basis)} – present`
                        : `${formatBoundary(period.from_date, period.from_basis)} – ${formatBoundary(
                            period.to_date,
                            period.to_basis,
                          )}`}
                    </Typography>
                    {period.seniority && (
                      <Chip
                        label={humanize(period.seniority)}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Panel>
  );
}
